import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { EntityType } from '../../objects/interface-library';
import { AttachDocumentDialogComponent } from '../../shared/components/attach-document-dialog/attach-document-dialog.component';
import { PermissionsStoreService } from '../permissions/permissions-store.service';
import {
  Capability,
  CapabilityDescriptor,
  CapabilityMatch,
} from './capability.types';

/**
 * The tool-manifest. Single source of truth for "things a user can accomplish"
 * and how to accomplish them. See capability.types.ts for the two-halves model.
 *
 * The only choke point that decides what the LLM (and the palette) may offer is
 * getEnabled() — it drops feature-flag-disabled and admin-only capabilities so a
 * disabled/forbidden destination can never be returned.
 */
@Injectable({ providedIn: 'root' })
export class CapabilityRegistryService {
  private permissionsStore = inject(PermissionsStoreService);

  /**
   * The catalog. Route strings must match src/app/app.routes.ts. Capabilities
   * whose target needs a service side-effect (the My Work approval queues, which
   * are not deep-linkable) set the signal BEFORE navigating so MyWorkComponent's
   * effect() renders the right list on arrival.
   */
  private readonly capabilities: Capability[] = [
    // ---- My Work approval queues (navigate + signal side-effect) ----
    {
      id: 'approve-timecards',
      title: 'Approve Timecards',
      description: 'Open the My Work queue for timecards awaiting my review or approval.',
      category: 'navigate',
      icon: 'fact_check',
      exampleUtterances: ['approve timecards', 'review timecards', 'timecard approvals', 'sign off timecards', 'timecards to approve'],
      isEnabled: (env) => !!env.timecards,
      execute: (ctx) => {
        ctx.myWorkFilter.setEntityType(EntityType.Timecard);
        ctx.router.navigate(['/securehome/my-work']);
      },
    },
    {
      id: 'approve-timesheets',
      title: 'Approve Timesheets',
      description: 'Open the My Work queue for timesheets awaiting my review, approval, or payment.',
      category: 'navigate',
      icon: 'schedule',
      exampleUtterances: ['approve timesheets', 'review timesheets', 'timesheet approvals', 'sign off on hours', 'timesheets to approve'],
      isEnabled: (env) => !!env.timesheets,
      execute: (ctx) => {
        ctx.myWorkFilter.setEntityType(EntityType.Timesheet);
        ctx.router.navigate(['/securehome/my-work']);
      },
    },
    {
      id: 'my-work',
      title: 'My Work',
      description: 'Open My Work — the items needing my attention across contracts, dates, and approvals.',
      category: 'navigate',
      icon: 'inbox',
      exampleUtterances: ['my work', 'what needs my attention', 'my tasks', 'my queue'],
      execute: (ctx) => ctx.router.navigate(['/securehome/my-work']),
    },

    // ---- Plain-route list destinations ----
    {
      id: 'go-contracts',
      title: 'Contracts',
      description: 'View the list of contracts.',
      category: 'navigate',
      icon: 'description',
      exampleUtterances: ['show contracts', 'go to contracts', 'contract list', 'find a contract'],
      execute: (ctx) => ctx.router.navigate(['/securehome/contracts']),
    },
    {
      id: 'go-parties',
      title: 'Vendors / Parties',
      description: 'View the list of vendors and parties.',
      category: 'navigate',
      icon: 'business',
      exampleUtterances: ['show vendors', 'parties', 'vendor list', 'find a vendor', 'suppliers'],
      execute: (ctx) => ctx.router.navigate(['/securehome/parties']),
    },
    {
      id: 'go-people',
      title: 'People',
      description: 'View the list of people.',
      category: 'navigate',
      icon: 'people',
      exampleUtterances: ['show people', 'people list', 'find a person', 'contacts'],
      execute: (ctx) => ctx.router.navigate(['/securehome/people']),
    },
    {
      id: 'go-baas',
      title: 'BAAs',
      description: 'View the list of Business Associate Agreements (BAAs).',
      category: 'navigate',
      icon: 'health_and_safety',
      exampleUtterances: ['show baas', 'business associate agreements', 'baa list'],
      execute: (ctx) => ctx.router.navigate(['/securehome/baas']),
    },
    {
      id: 'go-cois',
      title: 'COIs',
      description: 'View the list of Certificates of Insurance (COIs).',
      category: 'navigate',
      icon: 'verified_user',
      exampleUtterances: ['show cois', 'certificates of insurance', 'coi list', 'insurance certificates'],
      execute: (ctx) => ctx.router.navigate(['/securehome/cois']),
    },
    {
      id: 'go-provisions',
      title: 'Provisions',
      description: 'View the list of contract provisions and clauses.',
      category: 'navigate',
      icon: 'gavel',
      exampleUtterances: ['show provisions', 'provision list', 'clauses'],
      execute: (ctx) => ctx.router.navigate(['/securehome/provision-list']),
    },
    {
      id: 'go-critical-dates',
      title: 'Important Dates',
      description: 'View the list of important / critical dates and reminders.',
      category: 'navigate',
      icon: 'event',
      exampleUtterances: ['important dates', 'critical dates', 'reminders', 'key dates'],
      execute: (ctx) => ctx.router.navigate(['/securehome/critical-dates']),
    },
    {
      id: 'go-sites',
      title: 'Sites',
      description: 'View the list of sites / locations.',
      category: 'navigate',
      icon: 'location_on',
      exampleUtterances: ['show sites', 'locations', 'site list'],
      execute: (ctx) => ctx.router.navigate(['/securehome/sites']),
    },
    {
      id: 'go-legal-entities',
      title: 'Legal Entities',
      description: 'View the list of legal entities.',
      category: 'navigate',
      icon: 'apartment',
      exampleUtterances: ['legal entities', 'my organizations', 'entity list'],
      execute: (ctx) => ctx.router.navigate(['/securehome/legal-entities']),
    },
    {
      id: 'go-attachments',
      title: 'Attachments',
      description: 'View the list of file attachments.',
      category: 'navigate',
      icon: 'folder',
      exampleUtterances: ['attachments', 'files', 'documents list'],
      execute: (ctx) => ctx.router.navigate(['/securehome/attachments']),
    },
    {
      id: 'go-assessments',
      title: 'Assessments',
      description: 'View the list of risk / compliance assessments.',
      category: 'navigate',
      icon: 'rule',
      exampleUtterances: ['assessments', 'risk assessments', 'compliance assessments'],
      isEnabled: (env) => !!env.assessments,
      execute: (ctx) => ctx.router.navigate(['/securehome/assessments']),
    },
    {
      id: 'go-timesheets',
      title: 'Timesheets',
      description: 'Open the timesheets area (specs and submissions).',
      category: 'navigate',
      icon: 'schedule',
      exampleUtterances: ['timesheets', 'go to timesheets', 'timesheet specs'],
      isEnabled: (env) => !!env.timesheets,
      execute: (ctx) => ctx.router.navigate(['/securehome/timesheets']),
    },
    {
      id: 'go-timecards',
      title: 'Timecards',
      description: 'Open the timecards area (HCP timecard specs).',
      category: 'navigate',
      icon: 'timer',
      exampleUtterances: ['timecards', 'go to timecards', 'timecard specs'],
      isEnabled: (env) => !!env.timecards,
      execute: (ctx) => ctx.router.navigate(['/securehome/timecards']),
    },
    {
      id: 'go-workflows',
      title: 'Workflow',
      description: 'Open the workflow area to view and manage workflow requests.',
      category: 'navigate',
      // Matches the App Menu tile in secureHome.html so the palette row and the
      // tile read as the same destination.
      icon: 'account_tree',
      exampleUtterances: ['workflow', 'workflows', 'go to workflow', 'workflow requests', 'show me workflows'],
      isEnabled: (env) => !!env.workflowManagement,
      execute: (ctx) => ctx.router.navigate(['/securehome/workflows']),
    },

    // ---- Reports ----
    {
      id: 'go-reports',
      title: 'Reports',
      description: 'Open reports to run, view, or manage saved reports.',
      category: 'report',
      icon: 'assessment',
      exampleUtterances: ['run a report', 'show me reports', 'reporting', 'view reports'],
      execute: (ctx) => ctx.router.navigate(['/securehome/reports']),
    },
    {
      id: 'build-report',
      title: 'Build a Report',
      description: 'Open the report builder to create a new report.',
      category: 'report',
      icon: 'insights',
      exampleUtterances: ['build a report', 'create a report', 'new report', 'make a report'],
      execute: (ctx) => ctx.router.navigate(['/securehome/report-builder']),
    },

    // ---- Create flows ----
    {
      id: 'new-contract',
      title: 'New Contract',
      description: 'Start creating a new contract (optionally with AI intake).',
      category: 'create',
      icon: 'note_add',
      exampleUtterances: ['add a contract', 'new contract', 'create a contract', 'upload a contract'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-contract']),
    },
    {
      id: 'new-vendor',
      title: 'Add a Vendor',
      description: 'Start creating a new vendor / party record.',
      category: 'create',
      icon: 'add_business',
      exampleUtterances: ['add a vendor', 'create a vendor', 'new party', 'onboard a supplier'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-vendor-full-page']),
    },
    {
      id: 'new-person',
      title: 'Add a Person',
      description: 'Start creating a new person record.',
      category: 'create',
      icon: 'person_add',
      exampleUtterances: ['add a person', 'create a person', 'new contact'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-person-full-page']),
    },
    {
      id: 'new-baa',
      title: 'New BAA',
      description: 'Start creating a new Business Associate Agreement (BAA).',
      category: 'create',
      icon: 'health_and_safety',
      exampleUtterances: ['add a baa', 'new baa', 'create a business associate agreement'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-baa-full-page']),
    },
    {
      id: 'new-coi',
      title: 'New COI',
      description: 'Start creating a new Certificate of Insurance (COI).',
      category: 'create',
      icon: 'verified_user',
      exampleUtterances: ['add a coi', 'new coi', 'create a certificate of insurance'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-coi-full-page']),
    },
    {
      id: 'new-critical-date',
      title: 'Add an Important Date',
      description: 'Create a new important / critical date reminder.',
      category: 'create',
      icon: 'event_available',
      exampleUtterances: ['add a date', 'new reminder', 'create an important date', 'add a deadline'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-critical-date']),
    },
    {
      id: 'new-site',
      title: 'Add a Site',
      description: 'Start creating a new site / location record.',
      category: 'create',
      icon: 'add_location_alt',
      exampleUtterances: ['add a site', 'new site', 'create a location', 'new location'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-site']),
    },
    {
      id: 'new-legal-entity',
      title: 'Add a Legal Entity',
      description: 'Start creating a new legal entity record.',
      category: 'create',
      icon: 'domain',
      exampleUtterances: ['add a legal entity', 'new legal entity', 'create an organization'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-legal-entity']),
    },
    {
      id: 'new-workflow',
      title: 'New Workflow',
      description: 'Start creating a new workflow request.',
      category: 'create',
      icon: 'account_tree',
      // Deliberately NOT claiming "new workflow request": that is the visible
      // label of the Drop-and-Reveal intake modal (the button in every list
      // sidenav, reached here via 'intake-document'), so the phrase most likely
      // means that, not this entity form. Left unclaimed rather than hijacked.
      exampleUtterances: ['new workflow', 'create a workflow', 'start a workflow'],
      isEnabled: (env) => !!env.workflowManagement,
      execute: (ctx) => ctx.router.navigate(['/securehome/new-workflow']),
    },
    {
      /**
       * The only capability that isn't a navigation. An attachment needs a
       * target record, and from the palette we have no context at all — so this
       * opens a dialog that uploads the file, asks the intake AI what it is and
       * who it's between, and offers the matched party's contracts / BAAs / COIs
       * as destinations. "addendum" is listed on its own because the keyword
       * scorer has no stemming or synonyms: a word that isn't here scores zero.
       */
      id: 'attach-document',
      title: 'Add an Attachment',
      description: 'Upload a document and file it against a contract, party, BAA, COI, person, or user.',
      category: 'create',
      icon: 'attach_file',
      exampleUtterances: [
        'add an attachment',
        'add an addendum',
        'attach a file',
        'attach a document',
        'upload a document',
        'upload a file',
        'file a document',
        'add a document to a contract',
        'attach this to a contract',
      ],
      execute: (ctx) => {
        ctx.dialog.open(AttachDocumentDialogComponent, {
          width: '640px',
          maxWidth: '92vw',
          disableClose: true,
        });
      },
    },
    {
      id: 'intake-document',
      title: 'Process a Document with AI',
      description: 'Drop a document and let AI classify it and route you to the right intake form.',
      category: 'create',
      icon: 'auto_awesome',
      exampleUtterances: ['process a document', 'i have a new document', 'drop a file', 'ai intake', 'analyze a document'],
      execute: (ctx) => ctx.router.navigate(['/securehome/new-request']),
    },

    // ---- Admin ----
    {
      id: 'manage-users',
      title: 'Manage Users',
      description: 'Open the admin page to activate/deactivate and manage users.',
      category: 'admin',
      icon: 'manage_accounts',
      requiresAdmin: true,
      exampleUtterances: ['manage users', 'activate a user', 'user administration', 'add a user'],
      execute: (ctx) => ctx.router.navigate(['/securehome/manage-users']),
    },
    {
      id: 'proxy-assignments',
      title: 'Proxy Assignments',
      description: 'Open the admin page to grant or revoke proxy (act-on-behalf-of) authority.',
      category: 'admin',
      icon: 'switch_account',
      requiresAdmin: true,
      exampleUtterances: ['proxy assignments', 'grant a proxy', 'let someone submit on behalf of', 'delegate access'],
      execute: (ctx) => ctx.router.navigate(['/securehome/proxy-assignments']),
    },
    {
      id: 'permissions',
      title: 'Permissions',
      description: 'Open the admin permissions page.',
      category: 'admin',
      icon: 'admin_panel_settings',
      requiresAdmin: true,
      exampleUtterances: ['permissions', 'manage permissions', 'user roles'],
      execute: (ctx) => ctx.router.navigate(['/securehome/permissions']),
    },

    // ---- Param-carrying example (Phase 3 param extraction; safe fallback today) ----
    {
      id: 'open-contract-by-id',
      title: 'Open a Specific Contract',
      description: 'Open a specific contract by its id for viewing or editing.',
      category: 'navigate',
      icon: 'search',
      params: [{ name: 'contractId', type: 'entityRef', required: true, description: 'The contract id to open' }],
      exampleUtterances: ['open contract 12345', 'edit the Acme contract'],
      execute: (ctx, params) => {
        const id = params?.['contractId'];
        // Fallback: without a resolved id, land on the contracts list rather than a dead route.
        return id
          ? ctx.router.navigate(['/securehome/contract', id])
          : ctx.router.navigate(['/securehome/contracts']);
      },
    },
  ];

  /** Every capability, unfiltered. */
  all(): Capability[] {
    return this.capabilities;
  }

  /**
   * The capabilities the current user/org may actually reach — the ONLY set the
   * palette shows and the ONLY set sent to the backend. Drops feature-flag- and
   * admin-gated entries so a disabled/forbidden destination can never surface.
   */
  getEnabled(): Capability[] {
    const isAdmin = this.permissionsStore.isAdmin();
    return this.capabilities.filter((cap) => {
      if (cap.requiresAdmin && !isAdmin) return false;
      if (cap.isEnabled && !cap.isEnabled(environment)) return false;
      return true;
    });
  }

  /** The serializable, LLM-facing slice of the enabled capabilities. */
  getSerializableManifest(): CapabilityDescriptor[] {
    return this.getEnabled().map((cap) => ({
      id: cap.id,
      title: cap.title,
      description: cap.description,
      category: cap.category,
      ...(cap.icon ? { icon: cap.icon } : {}),
      exampleUtterances: cap.exampleUtterances,
      ...(cap.params ? { params: cap.params } : {}),
    }));
  }

  /** Look up a capability by id (used to validate a backend/stub decision). */
  get(id: string): Capability | undefined {
    return this.capabilities.find((c) => c.id === id);
  }

  /**
   * Client-side keyword scoring over the ENABLED capabilities. Powers the
   * palette's instant live filter and the Phase-1 resolve() stub. Returns
   * matches with score > 0, best first.
   */
  search(query: string): CapabilityMatch[] {
    const tokens = this.tokenize(query);
    if (tokens.length === 0) return [];
    return this.getEnabled()
      .map((capability) => ({ capability, score: this.scoreCapability(tokens, query, capability) }))
      .filter((m) => m.score > 0)
      .sort((a, b) => b.score - a.score);
  }

  private tokenize(text: string): string[] {
    return (text || '')
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length > 1);
  }

  /**
   * Simple, dependency-free relevance score. Weights an exact example-phrase
   * substring match highest, then title-token hits, then example/description
   * token hits. Good enough for the Phase-1 palette; Phase 2 replaces resolve()
   * with the LLM (this stays as the instant live-filter).
   */
  private scoreCapability(queryTokens: string[], rawQuery: string, cap: Capability): number {
    const q = (rawQuery || '').toLowerCase().trim();
    let score = 0;

    // Strong signal: a whole example phrase is contained in the query (or vice versa).
    for (const ex of cap.exampleUtterances) {
      const e = ex.toLowerCase();
      if (q && (q.includes(e) || e.includes(q))) {
        score += 100;
        break;
      }
    }

    const titleTokens = new Set(this.tokenize(cap.title));
    const exampleTokens = new Set(cap.exampleUtterances.flatMap((e) => this.tokenize(e)));
    const descTokens = new Set(this.tokenize(cap.description));

    for (const t of queryTokens) {
      if (titleTokens.has(t)) score += 10;
      if (exampleTokens.has(t)) score += 6;
      if (descTokens.has(t)) score += 2;
    }
    return score;
  }
}
